import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

const PRIORITY_OPTIONS = ['required', 'recommended', 'optional'];
const APPLICABLE_TO_OPTIONS = ['aggregate', 'specific', 'report'];

const jsonEditorSx = {
  '& .MuiInputBase-root': {
    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
    fontSize: '0.85rem',
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
  },
  '& .MuiInputBase-input': {
    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
    fontSize: '0.85rem',
  },
};

export interface ClarificationFieldFormData {
  id?: string;
  field: string;
  label: string;
  priority: string;
  examples: string[];
  defaultBehavior: string;
  applicableTo: string[];
  dynamicOptions: Record<string, any> | null;
  stage: number;
  valueMap: Record<string, any> | null;
  includeInQuery: boolean;
}

const VALUE_MAP_PLACEHOLDER = `{
  "key1": "value1",
  "key2": "value2"
}`;

interface CreateEditClarificationFieldModalProps {
  open: boolean;
  isEdit: boolean;
  onClose: () => void;
  onSave: (data: ClarificationFieldFormData) => void;
  saving: boolean;
  saveError: string | null;
}

const CreateEditClarificationFieldModal: React.FC<CreateEditClarificationFieldModalProps> = ({
  open,
  isEdit,
  onClose,
  onSave,
  saving,
  saveError,
}) => {
  const { selectedField, loading } = useAppSelector((state) => state.clarificationFields);

  const [formData, setFormData] = useState({
    field: '',
    label: '',
    priority: '',
    examples: '',
    defaultBehavior: '',
    applicableTo: [] as string[],
    dynamicOptionsJson: '',
    stage: 1,
    valueMapJson: '',
    includeInQuery: false,
  });
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [valueMapJsonError, setValueMapJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && selectedField) {
      setFormData({
        field: selectedField.field || '',
        label: selectedField.label || '',
        priority: selectedField.priority || '',
        examples: selectedField.examples.join(', '),
        defaultBehavior: selectedField.defaultBehavior || '',
        applicableTo: selectedField.applicableTo || [],
        dynamicOptionsJson: selectedField.dynamicOptions
          ? JSON.stringify(selectedField.dynamicOptions, null, 2)
          : '',
        stage: selectedField.stage ?? 1,
        valueMapJson: selectedField.valueMap
          ? JSON.stringify(selectedField.valueMap, null, 2)
          : '',
        includeInQuery: selectedField.includeInQuery ?? false,
      });
      setJsonError(null);
      setValueMapJsonError(null);
    } else if (!isEdit) {
      setFormData({
        field: '',
        label: '',
        priority: '',
        examples: '',
        defaultBehavior: '',
        applicableTo: [],
        dynamicOptionsJson: '',
        stage: 1,
        valueMapJson: '',
        includeInQuery: false,
      });
      setJsonError(null);
      setValueMapJsonError(null);
    }
  }, [selectedField, isEdit, open]);

  const handleChange = (field: string, value: string | string[] | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const examples = formData.examples
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    let dynamicOptions: Record<string, any> | null = null;
    if (formData.dynamicOptionsJson.trim()) {
      try {
        dynamicOptions = JSON.parse(formData.dynamicOptionsJson.trim());
        setJsonError(null);
      } catch {
        setJsonError('JSON inválido. Verifique el formato.');
        return;
      }
    }

    let valueMap: Record<string, any> | null = null;
    if (formData.valueMapJson.trim()) {
      try {
        valueMap = JSON.parse(formData.valueMapJson.trim());
        setValueMapJsonError(null);
      } catch {
        setValueMapJsonError('JSON inválido. Verifique el formato.');
        return;
      }
    }

    const data: ClarificationFieldFormData = {
      field: formData.field,
      label: formData.label,
      priority: formData.priority,
      examples,
      defaultBehavior: formData.defaultBehavior,
      applicableTo: formData.applicableTo,
      dynamicOptions,
      stage: formData.stage,
      valueMap,
      includeInQuery: formData.includeInQuery,
    };

    if (isEdit && selectedField) {
      data.id = selectedField.id;
    }

    onSave(data);
  };

  const handleFormatJson = () => {
    const value = formData.dynamicOptionsJson;
    if (!value || value.trim() === '') return;
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      setFormData((prev) => ({ ...prev, dynamicOptionsJson: formatted }));
      setJsonError(null);
    } catch {
      setJsonError('JSON inválido. No se puede formatear.');
    }
  };

  const handleFormatValueMapJson = () => {
    const value = formData.valueMapJson;
    if (!value || value.trim() === '') return;
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      setFormData((prev) => ({ ...prev, valueMapJson: formatted }));
      setValueMapJsonError(null);
    } catch {
      setValueMapJsonError('JSON inválido. No se puede formatear.');
    }
  };

  const isFormValid = formData.field && formData.label && formData.priority;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Editar Campo de Clarificación' : 'Crear Campo de Clarificación'}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}

            <TextField
              label="Campo"
              value={formData.field}
              onChange={(e) => handleChange('field', e.target.value)}
              fullWidth
              required
              disabled={saving}
            />

            <TextField
              label="Etiqueta"
              value={formData.label}
              onChange={(e) => handleChange('label', e.target.value)}
              fullWidth
              required
              multiline
              rows={2}
              disabled={saving}
            />

            <Autocomplete
              options={PRIORITY_OPTIONS}
              value={formData.priority || null}
              onChange={(_event, value) => handleChange('priority', value || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Prioridad"
                  required
                />
              )}
              disabled={saving}
            />

            <TextField
              label="Ejemplos (separados por comas)"
              value={formData.examples}
              onChange={(e) => handleChange('examples', e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={saving}
              helperText="Ingrese los ejemplos separados por comas"
            />

            <TextField
              label="Comportamiento por Defecto"
              value={formData.defaultBehavior}
              onChange={(e) => handleChange('defaultBehavior', e.target.value)}
              fullWidth
              disabled={saving}
            />

            <Autocomplete
              multiple
              options={APPLICABLE_TO_OPTIONS}
              value={formData.applicableTo}
              onChange={(_event, value) => handleChange('applicableTo', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Aplicable A"
                />
              )}
              disabled={saving}
            />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Dynamic Options (JSON)
                </Typography>
                <Button size="small" onClick={handleFormatJson} disabled={saving}>
                  Formatear JSON
                </Button>
              </Box>
              <TextField
                value={formData.dynamicOptionsJson}
                onChange={(e) => {
                  handleChange('dynamicOptionsJson', e.target.value);
                  if (jsonError) setJsonError(null);
                }}
                fullWidth
                multiline
                minRows={6}
                maxRows={16}
                disabled={saving}
                error={!!jsonError}
                helperText={jsonError}
                placeholder={'{\n  "prompt": "Estas son las ociones disponibles:",\n  "queryKey": "status",\n  "valueField": "name",\n  "displayField": "name",\n  "searchQueryKey": "search_status"\n}'}
                sx={jsonEditorSx}
              />
            </Box>

            <TextField
              label="Stage"
              type="number"
              value={formData.stage}
              onChange={(e) => handleChange('stage', Math.max(1, parseInt(e.target.value) || 1))}
              fullWidth
              disabled={saving}
              inputProps={{ min: 1 }}
              helperText="Valor mínimo: 1"
            />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Value Map (JSON)
                </Typography>
                <Button size="small" onClick={handleFormatValueMapJson} disabled={saving}>
                  Formatear JSON
                </Button>
              </Box>
              <TextField
                value={formData.valueMapJson}
                onChange={(e) => {
                  handleChange('valueMapJson', e.target.value);
                  if (valueMapJsonError) setValueMapJsonError(null);
                }}
                fullWidth
                multiline
                minRows={6}
                maxRows={16}
                disabled={saving}
                error={!!valueMapJsonError}
                helperText={valueMapJsonError}
                placeholder={VALUE_MAP_PLACEHOLDER}
                sx={jsonEditorSx}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.includeInQuery}
                  onChange={(e) => handleChange('includeInQuery', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Include In Query"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !isFormValid}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditClarificationFieldModal;
